import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Shield, Eye, Lock, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Eye className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Privacy Policy
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Last Updated: September 27, 2024
          </p>
        </div>

        <Card className="shadow-xl border-0 mb-8">
          <CardHeader className="bg-green-50 border-b">
            <CardTitle className="flex items-center gap-3 text-green-800">
              <Shield className="w-6 h-6" />
              Key Privacy Highlights
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-green-50">
            <div className="space-y-3 text-green-800 font-medium">
              <p className="flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                We do not store or save customer-uploaded data (like bank statements or tax documents).
              </p>
              <p className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                We only collect minimal account and usage information to run the Service.
              </p>
              <p className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                We use strong safeguards to protect your information.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <div className="prose prose-slate max-w-none" style={{fontFamily: 'Inter, sans-serif'}}>
              <p className="text-lg text-slate-700 mb-8">
                Tax Helper Pros ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our application, website, and related services (collectively, the "Service"). By using the Service, you agree to the terms of this Privacy Policy.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">1. Information We Collect</h2>
              <p className="text-slate-700 mb-4">We may collect:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Account Information:</strong> Such as your email address and password (if you create an account).</li>
                <li><strong>Device & Usage Information:</strong> Such as IP address, browser type, device type, and usage statistics.</li>
              </ul>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-6">
                <p className="text-blue-800 font-medium">
                  <strong>Important:</strong> We do not store or save customer-uploaded data (including bank statements, tax documents, or other sensitive financial information) after it is processed within the Service. Your uploaded files are processed transiently to generate results and then immediately deleted or anonymized.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Provide, operate, and improve the Service.</li>
                <li>Communicate with you about your account or Service updates.</li>
                <li>Maintain security and prevent fraud.</li>
              </ul>
              <p className="text-slate-700 font-medium">
                Because we do not store or save customer-uploaded data, we do not use uploaded data for analytics, advertising, or other secondary purposes.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">3. Data Security</h2>
              <p className="text-slate-700">
                We implement reasonable physical, technical, and administrative measures to protect your information during transmission and processing. However, no method of transmission or storage is completely secure. You use the Service at your own risk.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">4. Third-Party Services</h2>
              <p className="text-slate-700">
                We may use third-party service providers to support the Service (e.g., hosting, analytics). These providers are only permitted to use your information as necessary to provide services to us and are bound by confidentiality obligations.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">5. Children's Privacy</h2>
              <p className="text-slate-700">
                The Service is not directed to individuals under the age of 18. We do not knowingly collect information from children.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">6. Your Rights</h2>
              <p className="text-slate-700 mb-4">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Access and receive a copy of the personal information we hold about you (limited to account information we retain).</li>
                <li>Request correction or deletion of your account information.</li>
                <li>Withdraw consent to our processing of your account information.</li>
              </ul>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-6">
                <p className="text-yellow-800 font-medium">
                  <strong>Note:</strong> Because we do not retain customer-uploaded data, we cannot retrieve or delete uploaded data after processing.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">7. Data Retention</h2>
              <p className="text-slate-700">
                We retain only the minimal account information necessary to operate the Service. We do not retain customer-uploaded data beyond the time needed to process it.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">8. Changes to This Privacy Policy</h2>
              <p className="text-slate-700">
                We may update this Privacy Policy from time to time. If we make material changes, we will notify you by updating the "Last Updated" date above or by other reasonable means. Your continued use of the Service constitutes your acceptance of the revised Privacy Policy.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">9. Contact Us</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 mb-2">If you have questions or concerns about this Privacy Policy or our practices, please contact us at:</p>
                <p className="text-blue-800 font-medium">privacy@taxhelperpros.com</p>
                <p className="text-blue-800">Tax Helper Pros LLC</p>
                <p className="text-blue-800">United States</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Protection Guarantee */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-green-800 text-center justify-center">
              <Lock className="w-8 h-8" />
              Our Data Protection Promise
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-4 text-green-800">
              <p className="text-lg font-semibold">
                Your sensitive financial data is processed securely and then immediately deleted.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-semibold mb-2">Encrypted Processing</h4>
                  <p className="text-sm">All data is encrypted during transmission and processing</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Trash2 className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-semibold mb-2">Immediate Deletion</h4>
                  <p className="text-sm">Files are deleted immediately after processing</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-semibold mb-2">No Storage</h4>
                  <p className="text-sm">We never store your sensitive financial documents</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;